// src/hooks/useTickets.js
import { useState, useEffect, useCallback } from 'react';
import { NOCOBASE_BASE_URL, NOCOBASE_TOKEN } from '../services/api';

const NOCOBASE_URL = `${NOCOBASE_BASE_URL}/api/tickets:list`;

/**
 * Normalises a raw NocoBase ticket record into a shape the UI can consume.
 *
 * Actual API field names (from real payload):
 *   ticketid          → numeric ID
 *   Creatoremail      → submitter email
 *   Summary           → short summary text
 *   description       → long description
 *   status            → Open | In Progress | On hold | Closed | null
 *   tickettype        → category string
 *   impactedsystem    → string[] | null
 *   devusername       → assignee name
 *   priority          → High | Medium | Low | null
 *   Rootcause         → root cause text
 *   Rootcausecategory → root cause category
 *   createdAt         → ISO timestamp
 *
 * @param {object} raw - Raw record from the API
 * @returns {object} Normalised ticket
 */
function normaliseTicket(raw) {
  const systemArr = Array.isArray(raw.impactedsystem) ? raw.impactedsystem : [];
  
  const {
    ticketid,
    status = 'Open',
    tickettype = '—',
    devusername = '—',
    createdAt = null,
    loanid = '—',
    Summary = '—',
    description = '—',
    priority = '—',
    Rootcause = '—',
    Rootcausecategory = '—',
    Resolution = '—',
    Creatoremail = '',
    Creator = '',
    Country = '',
    environment = '',
    platform = '',
    attachement = []
  } = raw;

  const impactedSystem = systemArr.length > 0 ? systemArr.join(', ') : tickettype;
  const rootCause = Rootcause !== '—' ? Rootcause : Rootcausecategory;
  const summary = Summary !== '—' ? Summary : description;

  return {
    _key: ticketid,
    ticketId: `TKT-${ticketid}`,
    status,
    ticketType: tickettype,
    impactedSystem,
    assigneeDev: devusername,
    createdAt,
    loanId: loanid,
    summary,
    description,
    priority,
    rootCause,
    resolution: Resolution,
    creatorEmail: Creatoremail,
    creator: Creator,
    country: Country,
    environment,
    platform,
    attachments: attachement
  };
}

/**
 * Fetches a single page from the NocoBase tickets list API.
 * @param {number} page
 * @param {string} email
 * @returns {Promise<{ data: object[], meta: object }>}
 */
async function fetchPage(page, email) {
  // Use NocoBase JSON filter to ask the server to filter by Creatoremail.
  // We still do a client-side pass as well since some tickets have Creatoremail: null.
  const filter = JSON.stringify({ Creatoremail: { $eq: email } });

  const params = new URLSearchParams({
    filter,
    page: String(page),
    pageSize: '100',
    appends: 'attachement',
  });

  const res = await fetch(`${NOCOBASE_URL}?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${NOCOBASE_TOKEN}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch tickets (page ${page}): ${res.status} ${res.statusText}`);
  }

  return res.json();
}

/**
 * Custom hook that fetches all open tickets belonging to the logged-in user.
 *
 * Filtering strategy:
 *   1. Server-side: sends a NocoBase JSON filter on `Creatoremail`.
 *   2. Client-side: falls back to matching `creatorEmail` case-insensitively,
 *      in case some records store the email under a different casing.
 *   3. Status filter: removes any ticket with status "Closed" or "Resolved".
 *
 * @param {string|null} email - The current user's email address
 * @returns {{ tickets: object[], loading: boolean, error: string|null, refetch: Function }}
 */
export function useTickets(email) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTickets = useCallback(async () => {
    if (!email) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch page 1 to discover total page count
      const firstResponse = await fetchPage(1, email);
      const totalPages = firstResponse?.meta?.totalPage ?? 1;
      let allRaw = Array.isArray(firstResponse.data) ? firstResponse.data : [];

      // Fetch remaining pages in parallel if needed
      if (totalPages > 1) {
        const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const rest = await Promise.all(pageNumbers.map((p) => fetchPage(p, email)));
        rest.forEach((r) => {
          if (Array.isArray(r.data)) allRaw = allRaw.concat(r.data);
        });
      }

      const emailLower = email.toLowerCase();

      const normalised = allRaw
        .map(normaliseTicket)
        // Client-side email guard (catches records where server filter missed due to null)
        .filter((t) => {
          if (!t.creatorEmail) return false;
          return t.creatorEmail.toLowerCase() === emailLower;
        })
        // Only show active tickets (Open, In Progress, On hold)
        .filter((t) => {
          const s = (t.status || '').toLowerCase().trim();
          return s !== 'closed' && s !== 'cancelled' && s !== 'resolved';
        });

      setTickets(normalised);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return { tickets, loading, error, refetch: fetchTickets };
}

/**
 * Custom hook that fetches all closed/resolved tickets belonging to the logged-in user.
 *
 * Mirrors `useTickets` but keeps only tickets with status "Closed" or "Resolved".
 *
 * @param {string|null} email - The current user's email address
 * @returns {{ tickets: object[], loading: boolean, error: string|null, refetch: Function }}
 */
export function useClosedTickets(email) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTickets = useCallback(async () => {
    if (!email) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch page 1 to discover total page count
      const firstResponse = await fetchPage(1, email);
      const totalPages = firstResponse?.meta?.totalPage ?? 1;
      let allRaw = Array.isArray(firstResponse.data) ? firstResponse.data : [];

      // Fetch remaining pages in parallel if needed
      if (totalPages > 1) {
        const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const rest = await Promise.all(pageNumbers.map((p) => fetchPage(p, email)));
        rest.forEach((r) => {
          if (Array.isArray(r.data)) allRaw = allRaw.concat(r.data);
        });
      }

      const emailLower = email.toLowerCase();

      const normalised = allRaw
        .map(normaliseTicket)
        // Client-side email guard
        .filter((t) => {
          if (!t.creatorEmail) return false;
          return t.creatorEmail.toLowerCase() === emailLower;
        })
        // Only show closed / cancelled / resolved tickets
        .filter((t) => {
          const s = (t.status || '').toLowerCase().trim();
          return s === 'closed' || s === 'cancelled' || s === 'resolved';
        });

      setTickets(normalised);
    } catch (err) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return { tickets, loading, error, refetch: fetchTickets };
}
